import { RocketIcon } from '@/components/icons/RocketIcon'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Form } from '@/components/ui/form'
import { FormInput } from '@/components/ui/form/FormInput'
import { authClient } from '@/lib/auth/client'
import { zodResolver } from '@hookform/resolvers/zod'
import { createFileRoute, Link } from '@tanstack/react-router'
import { Sparkles, TrendingUp, Zap } from 'lucide-react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'

export const Route = createFileRoute('/_unauthRedirect/signup/')({
  component: RouteComponent,
})

function RouteComponent() {
  const signupSchema = z
    .object({
      name: z
        .string()
        .min(2, 'Full name must be at least 2 characters')
        .max(100, 'Full name is too long'),
      email: z.email('Enter a valid email address'),
      password: z
        .string()
        .min(8, 'Password must be at least 8 characters')
        .max(100, 'Password is too long'),
      confirmPassword: z.string(),
    })
    .refine((data) => data.password === data.confirmPassword, {
      message: 'Passwords do not match',
      path: ['confirmPassword'],
    })

  type SignupSchema = z.infer<typeof signupSchema>

  const form = useForm<SignupSchema>({
    resolver: zodResolver(signupSchema),
    defaultValues: {
      name: '',
      email: '',
      password: '',
      confirmPassword: '',
    },
  })
  const [isLoading, setIsLoading] = useState(false)

  const onSubmit = (data: SignupSchema) => {
    setIsLoading(true)
    authClient.signUp
      .email(
        {
          email: data.email,
          password: data.password,
          name: data.name,
        },
        {
          onError: (ctx) => {
            toast.error(`Sign up failed: ${ctx.error.message}`)
          },
        },
      )
      .finally(() => {
        setIsLoading(false)
      })
  }

  return (
    <div className="relative flex min-h-screen overflow-hidden">
      {/* Animated gradient background */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(59,130,246,0.1),transparent_50%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_80%,rgba(147,51,234,0.1),transparent_50%)]" />
      </div>

      {/* Floating decorative elements */}
      <div className="absolute top-20 left-10 w-20 h-20 bg-blue-400/20 rounded-full blur-2xl animate-pulse" />
      <div className="absolute bottom-40 right-20 w-32 h-32 bg-purple-400/20 rounded-full blur-2xl animate-pulse delay-1000" />
      <div className="absolute top-1/2 right-1/4 w-24 h-24 bg-indigo-400/20 rounded-full blur-2xl animate-pulse delay-500" />

      <div className="relative z-10 flex w-full max-w-7xl mx-auto">
        {/* Left side - Benefits section */}
        <div className="hidden lg:flex lg:w-1/2 items-center justify-center p-12">
          <div className="max-w-lg">
            <div className="mb-8">
              <div className="flex items-center gap-4 mb-4">
                <RocketIcon className="w-14 h-14 text-blue-600" />
                <span className="text-4xl font-bold bg-blue-600 bg-clip-text text-transparent">
                  Rocket Rank
                </span>
              </div>
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Skyrocket to the Top
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                Join thousands of businesses who are dominating search rankings
                and growing their audience exponentially.
              </p>
            </div>

            {/* Feature list */}
            <div className="space-y-6">
              <div className="flex items-start gap-4 group">
                <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg transition-transform">
                  <Sparkles className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">
                    AI-Powered Content Generation
                  </h3>
                  <p className="text-sm text-gray-600">
                    Create SEO-optimized articles in minutes, not hours
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4 group">
                <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg transition-transform">
                  <TrendingUp className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">
                    Rank Faster, Rank Higher
                  </h3>
                  <p className="text-sm text-gray-600">
                    Data-driven insights to dominate search results
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4 group">
                <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg transition-transform">
                  <Zap className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">
                    Automated Publishing
                  </h3>
                  <p className="text-sm text-gray-600">
                    Set it and forget it - we handle the heavy lifting
                  </p>
                </div>
              </div>
            </div>

            {/* Social proof */}
            <div className="mt-10 pt-8 border-t border-gray-200">
              <p className="text-sm text-gray-600 italic">
                "Rocket Rank transformed our content strategy. We went from page
                10 to page 1 in just 3 months!"
              </p>
            </div>
          </div>
        </div>

        {/* Right side - Sign up form */}
        <div className="w-full lg:w-1/2 flex items-center justify-center p-4 sm:p-8">
          <div className="w-full max-w-md">
            {/* Mobile logo */}
            <div className="lg:hidden mb-8 text-center">
              <div className="flex justify-center mb-4">
                <RocketIcon className="w-12 h-12 text-blue-600" />
              </div>
              <h1 className="mb-2 text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                Join Rocket Rank
              </h1>
              <p className="text-gray-600">
                Start dominating search rankings today
              </p>
            </div>

            {/* Sign Up Form */}
            <Card className="border-0 shadow-2xl bg-white/80 backdrop-blur-sm">
              <CardHeader className="space-y-1 pb-6">
                <CardTitle className="text-2xl text-center font-bold">
                  Create Your Account
                </CardTitle>
                <CardDescription className="text-center text-base">
                  Get started in 60 seconds
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Form {...form}>
                  <form
                    onSubmit={form.handleSubmit(onSubmit)}
                    className="space-y-4"
                  >
                    <FormInput
                      formInputProps={{
                        type: 'text',
                        required: true,
                        className:
                          'h-12 border-gray-200 focus:border-blue-500 transition-colors',
                      }}
                      control={form.control}
                      name="name"
                      label="Full Name"
                      placeholder="John Doughnut"
                    />

                    <FormInput
                      formInputProps={{
                        type: 'email',
                        required: true,
                        className:
                          'h-12 border-gray-200 focus:border-blue-500 transition-colors',
                      }}
                      control={form.control}
                      name="email"
                      label="Email Address"
                      placeholder="john@example.com"
                    />

                    <FormInput
                      formInputProps={{
                        type: 'password',
                        required: true,
                        className:
                          'h-12 border-gray-200 focus:border-blue-500 transition-colors',
                      }}
                      control={form.control}
                      name="password"
                      label="Password"
                      placeholder="Create a password"
                    />

                    <FormInput
                      formInputProps={{
                        type: 'password',
                        required: true,
                        className:
                          'h-12 border-gray-200 focus:border-blue-500 transition-colors',
                      }}
                      control={form.control}
                      name="confirmPassword"
                      label="Confirm Password"
                      placeholder="Confirm your password"
                    />

                    <Button
                      type="submit"
                      className="w-full h-12 font-semibold shadow-lg hover:shadow-xl transition-all transform"
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <span className="flex items-center gap-2">
                          <span className="animate-spin">⚡</span>
                          Creating Your Account...
                        </span>
                      ) : (
                        <span className="flex items-center gap-2">
                          Create Account & Start Growing
                        </span>
                      )}
                    </Button>
                  </form>
                </Form>

                <div className="relative my-6">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t border-gray-200" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-white px-2 text-gray-500">
                      Or continue with
                    </span>
                  </div>
                </div>

                <Button
                  type="button"
                  variant="outline"
                  className="w-full h-12 border-gray-300 hover:bg-gray-50 transition-colors"
                  onClick={() => {
                    authClient.signIn.social({
                      provider: 'google',
                      callbackURL: window.location.origin,
                    })
                  }}
                >
                  <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                    <path
                      fill="#4285F4"
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    />
                    <path
                      fill="#34A853"
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    />
                    <path
                      fill="#FBBC05"
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    />
                    <path
                      fill="#EA4335"
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    />
                  </svg>
                  Continue with Google
                </Button>

                <div className="mt-6 text-center">
                  <p className="text-sm text-gray-600">
                    Already have an account?{' '}
                    <Link
                      to="/signin"
                      className="font-semibold text-blue-600 hover:text-blue-700 underline-offset-4 hover:underline"
                    >
                      Sign in
                    </Link>
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Terms */}
            <div className="mt-6 text-center">
              <p className="text-xs text-gray-500">
                By creating an account, you agree to our{' '}
                <a
                  href="https://userocketrank.com/terms-of-service"
                  className="text-blue-600 hover:text-blue-700 underline underline-offset-2"
                >
                  Terms of Service
                </a>{' '}
                and{' '}
                <a
                  href="https://userocketrank.com/privacy-policy"
                  className="text-blue-600 hover:text-blue-700 underline underline-offset-2"
                >
                  Privacy Policy
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
